// Design A ("Corporate Band") Sales Return -- used ONLY for Print Preview
// (via SalesReturnPrint.jsx) when this company's institution_profile.
// print_template is 'design_a'. Mirrors
// backend/src/pdfTemplates/designA/salesReturnAdapter.js's config mapping
// exactly. SalesReturnVoucherBody.jsx (legacy, still used for the live
// entry-screen preview and for the 'legacy' print_template) is untouched.
//
// Carried over from the legacy template (see salesReturnAdapter.js for the
// full rationale): no Paid/Due rows, no bank details (money is going back
// to the customer); CGST/SGST/IGST tax split mirrors Sales Invoice;
// individual-mode row quantity reads sale_r_qty, not item_qty.
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { numberFormat, numberFormatTax, formatToCompanyTimezone } from "../../../lib/functions";
import { convertAmountToWordsDesignA } from "../../../lib/designAWords";
import { fitItemName } from "../../../lib/designAItemFit";
import { formatPercent } from "../../../lib/designANumberFormat";
import { getsalesReturnRecordApi } from "../../../apiservice/sales";
import { getInstitutionApi } from "../../../apiservice/setting";
import { selectAuthInfo } from "../../Auth/authSelectors";
import VoucherBodyDesignA from "../../../components/common/VoucherBodyDesignA";

// Row budgets recalibrated for the taller "rich item" row (name line + an
// optional batch/description sub-line, see da-table--rich-item in
// VoucherBodyDesignA.css) -- ~42px/row vs. the default 28px, so ~2/3 as many
// rows fit per page. Mirrors salesInvoiceAdapter.js's own recalibration.
const SALES_RETURN_ROW_BUDGETS = { single: 10, first: 16, middle: 20, last: 9 };

// Same fixed-layout column widths as salesReturnAdapter.js -- see that
// file's comment for the reasoning. Keep both in sync.
const PAGE_CONTENT_WIDTH = 698;
const COL_WIDTH = { hash: 28, hsn: 72, rate: 100, per: 40, qty: 44, disc: 64, tax: 54, amount: 104 };
const CELL_PADDING = 16;
const CHAR_WIDTH_PX = 5.3;

function ItemNameCell({ name, maxChars }) {
  const fit = fitItemName(name, { maxChars });
  return <span className="da-item-name" style={{ fontSize: `${fit.fontSizePercent}%` }}>{fit.text}</span>;
}

function ItemCell({ name, maxChars, batchName, description }) {
  const subParts = [];
  if (batchName) subParts.push(`Batch: ${batchName}`);
  if (description) subParts.push(description);
  return (
    <div className="da-item-cell">
      <ItemNameCell name={name} maxChars={maxChars} />
      {subParts.length > 0 && <div className="da-item-sub">{subParts.join(" · ")}</div>}
    </div>
  );
}

export default function SalesReturnVoucherBodyDesignA({ sale_r_id, showStamp = false, showSignature = false }) {
  const [data, setData] = useState(null);
  const [institution, setInstitution] = useState(null);
  const authInfo = useSelector(selectAuthInfo);

  useEffect(() => {
    if (!sale_r_id) return;
    getsalesReturnRecordApi({ sale_r_id, from: "voucher" }).then((res) => {
      if (res?.data?.length) setData(res.data[0]);
    });
  }, [sale_r_id]);

  useEffect(() => {
    getInstitutionApi().then((res) => setInstitution(res?.data || null)).catch(() => {});
  }, []);

  if (!data) return null;

  const record = data;
  const currency = authInfo?.currency;
  const country = authInfo?.country || "";
  const isIndia = country.toLowerCase() === "india";
  const isIndiaGst = authInfo?.gst_enabled === "yes";
  const taxLabel = isIndia ? "GST" : "VAT";
  const isGSTDisabled = isIndia && authInfo?.gst_enabled == "no";
  const isUAE = country.trim().toLowerCase() === "united arab emirates";
  const subscriptionType = (authInfo?.subscription_type || "").toLowerCase();
  const isPharma = subscriptionType.includes("pharma") || subscriptionType.includes("pharmabooks");
  const accMgrLabel = isUAE && isPharma ? "Rep." : "Account Manager";
  const taxRegLabel = isUAE ? "TRN No" : "Tax Reg No";

  const isIndividual = record.is_cal_type === "individual";
  const items = record.details || [];

  // HSN/SAC is optional on the item master -- show the column only when at
  // least one item on this voucher actually has a code.
  const showHSN = isIndia && isIndiaGst && items.some((d) => d.hsn_sac_code);

  const usedWidth =
    COL_WIDTH.hash + (showHSN ? COL_WIDTH.hsn : 0) + COL_WIDTH.rate + COL_WIDTH.per + COL_WIDTH.qty +
    (isIndividual ? COL_WIDTH.disc : 0) + (isIndividual && !isGSTDisabled ? COL_WIDTH.tax : 0) + COL_WIDTH.amount;
  const itemMaxChars = Math.max(20, Math.floor((PAGE_CONTENT_WIDTH - usedWidth - CELL_PADDING) / CHAR_WIDTH_PX));

  const columns = [
    { label: "#", width: `${COL_WIDTH.hash}px` },
    { label: "Item & Description", cellClassName: "da-item-td" },
  ];
  if (showHSN) columns.push({ label: "HSN/SAC", width: `${COL_WIDTH.hsn}px` });
  columns.push({ label: "Rate", align: "right", width: `${COL_WIDTH.rate}px` });
  columns.push({ label: "Per", align: "right", width: `${COL_WIDTH.per}px` });
  columns.push({ label: "Qty", align: "right", width: `${COL_WIDTH.qty}px` });
  if (isIndividual) {
    columns.push({ label: "Disc %", align: "right", width: `${COL_WIDTH.disc}px` });
    if (!isGSTDisabled) columns.push({ label: `${taxLabel} %`, align: "right", width: `${COL_WIDTH.tax}px` });
    columns.push({ label: "Total", align: "right", width: `${COL_WIDTH.amount}px` });
  } else {
    columns.push({ label: "Amount", align: "right", width: `${COL_WIDTH.amount}px` });
  }

  const rows = items.map((d, i) => {
    const cells = [
      String(i + 1),
      <ItemCell key="n" name={d.item_name} maxChars={itemMaxChars} batchName={d.batch_name} description={d.item_description} />,
    ];
    if (showHSN) cells.push(d.hsn_sac_code);
    cells.push(<span className="da-num-cell" key="r">{numberFormat(d.item_rate)}</span>);
    cells.push(<span className="da-num-cell" key="p">{d.unit_symbol}</span>);

    const qty = Number((isIndividual ? d.sale_r_qty : d.item_qty) || 0);
    cells.push(<span className="da-num-cell" key="q">{formatPercent(qty)} {d.unit_symbol}</span>);

    let rawAmount;
    if (isIndividual) {
      cells.push(<span className="da-num-cell" key="d">{formatPercent(d.item_discount_per)}</span>);
      if (!isGSTDisabled) cells.push(<span className="da-num-cell" key="t">{formatPercent(d.item_tax_per)}</span>);
      let total = qty * Number(d.item_rate || 0);
      total -= (Number(d.item_discount_per || 0) / 100) * total;
      total += (Number(d.item_tax_per || 0) / 100) * total;
      rawAmount = total;
    } else {
      rawAmount = Number(d.item_total || 0);
    }
    cells.push(<span className="da-amount-cell" key="a">{numberFormat(rawAmount)}</span>);

    return { cells, isBonus: false, rawAmount };
  });

  const totals = items.reduce(
    (acc, d) => {
      const qty = Number((isIndividual ? d.sale_r_qty : d.item_qty) || 0);
      const rate = Number(d.item_rate || 0);
      const base = qty * rate;
      const discountAmt = (Number(d.item_discount_per || 0) / 100) * base;
      const afterDiscount = base - discountAmt;
      const taxAmt = (Number(d.item_tax_per || 0) / 100) * afterDiscount;
      acc.subTotal += base;
      acc.discountTotal += discountAmt;
      acc.grandTotal += afterDiscount + taxAmt;
      return acc;
    },
    { subTotal: 0, discountTotal: 0, grandTotal: 0 }
  );

  const grandTotalInWords = isIndividual ? totals.grandTotal : record.total_amount || 0;

  const totalsRows = [];
  totalsRows.push({ label: "Sub Total", value: numberFormat(isIndividual ? totals.subTotal : record.sub_total) });
  totalsRows.push({ label: "Discount", value: `- ${numberFormat(isIndividual ? totals.discountTotal : record.total_discount)}`, credit: true });

  let taxSummary = null;
  if (isIndiaGst) {
    if (record.place_of_supply_type === "intra") {
      totalsRows.push({ label: "CGST", value: numberFormatTax(record.cgst_amount) });
      totalsRows.push({ label: "SGST", value: numberFormatTax(record.sgst_amount) });
      taxSummary = { mode: "cgst_sgst", rows: [{ rate: "", taxable: numberFormat(totals.subTotal - totals.discountTotal), cgst: numberFormatTax(record.cgst_amount), sgst: numberFormatTax(record.sgst_amount) }] };
    }
    if (record.place_of_supply_type === "inter") {
      totalsRows.push({ label: "IGST", value: numberFormatTax(record.igst_amount) });
      taxSummary = { mode: "igst", rows: [{ rate: "", taxable: numberFormat(totals.subTotal - totals.discountTotal), igst: numberFormatTax(record.igst_amount) }] };
    }
    if (record.gst_calculated === "no") {
      totalsRows.push({ label: `Total ${taxLabel}`, value: numberFormat(record.total_tax) });
    }
  } else if (!isIndia) {
    totalsRows.push({ label: `Total ${taxLabel}`, value: numberFormat(record.total_tax) });
  }

  const config = {
    docTitle: "SALES RETURN",
    docNumber: record.sale_r_voucher_no,
    institution: { logoUrl: institution?.logo, companyName: institution?.name || "Institution", companyGstin: isIndia ? institution?.vat_license : null },
    party: {
      billToLabel: "Bill To",
      billTo: {
        name: record.acc_name,
        lines: [
          record.acc_mgr ? `${accMgrLabel}: ${record.acc_mgr}` : null,
          record.address ? `Address: ${record.address}` : null,
          record.contact_no ? `Contact No: +${record.contact_no}` : null,
          record.vat_no ? `${taxRegLabel}: ${record.vat_no}` : null,
          record.customer_po_ref ? `Customer PO Ref: ${record.customer_po_ref}` : null,
          record.payment_terms ? `Payment Terms: ${record.payment_terms} Days` : null,
        ].filter(Boolean),
      },
      fromLabel: "From",
      from: {
        name: institution?.name || "Institution",
        lines: [
          institution?.street_address || null,
          institution?.phone_no ? `+${institution.phone_no}` : null,
        ].filter(Boolean),
      },
      metaLabel: "Return Details",
      meta: [
        { label: "Return Date", value: (formatToCompanyTimezone(record.created_date) || "").split(" ")[0] },
        ...(institution?.vat_license ? [{ label: taxRegLabel, value: institution.vat_license }] : []),
        ...(record.licence_no ? [{ label: "License No", value: record.licence_no }] : []),
      ],
    },
    columns,
    rows,
    amountFn: (slice) => numberFormat(slice.reduce((sum, r) => sum + (r.rawAmount || 0), 0)),
    totalItems: items.length,
    totalQty: items.reduce((s, d) => s + (Number(isIndividual ? d.sale_r_qty : d.item_qty) || 0), 0),
    totalsRows,
    grandTotalLabel: `Grand Total (${currency || ""})`,
    grandTotalValue: numberFormat(record.total_amount),
    paidValue: null,
    dueValue: null,
    amountInWordsText: convertAmountToWordsDesignA(grandTotalInWords, { country, currency }),
    bankDetails: null,
    taxSummary,
    terms: record.narration ? `Description: ${record.narration}` : "Thank you for your business.",
    signatory: {
      companyName: institution?.name || "Institution",
      stampUrl: showStamp ? institution?.stamp : null,
      signatureUrl: showSignature ? institution?.signature : null,
    },
    bonusNote: null,
    rowBudgets: SALES_RETURN_ROW_BUDGETS,
    tableClassName: "da-table--rich-item",
  };

  return <VoucherBodyDesignA config={config} />;
}
